/**
 * @fileoverview Users API Endpoints
 * @author Hospital Room Scheduler Team
 * @created 08-10-2025 02:30:00
 * 
 * API client functions for user management operations.
 * Handles HTTP requests to backend user endpoints with proper error handling.
 * 
 * Features:
 * - User CRUD operations
 * - Search and filtering
 * - Role management
 * - Session management
 * - Proper TypeScript types
 * - Error handling and validation
 */

import { api } from '../index';

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

/**
 * User search and filter parameters
 */
export interface GetUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: 'ADMIN' | 'COORDINATOR' | 'DOCTOR';
  isActive?: boolean;
}

/**
 * User data structure
 */
export interface User {
  id: string;
  name: string;
  email: string;
  role: 'ADMIN' | 'COORDINATOR' | 'DOCTOR';
  specialty?: string;
  department?: string;
  isActive: boolean;
  lastLogin?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Users API response with pagination
 */
export interface UsersResponse {
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

/**
 * Create user data
 */
export interface CreateUserData {
  name: string;
  email: string;
  role: 'ADMIN' | 'COORDINATOR' | 'DOCTOR';
  specialty?: string;
  department?: string;
}

/**
 * Update user data
 */
export interface UpdateUserData {
  name?: string;
  email?: string;
  specialty?: string;
  department?: string;
}

/**
 * User session data
 */
export interface UserSession {
  id: string;
  userId: string;
  deviceInfo: string;
  ipAddress: string;
  userAgent: string;
  isActive: boolean;
  lastActivity: string;
  createdAt: string;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Get users with search and filtering
 * 
 * @param params - Search and filter parameters
 * @returns Promise with users data and pagination
 * 
 * @example
 * ```tsx
 * const users = await usersApi.getUsers({ 
 *   role: 'DOCTOR', 
 *   search: 'john',
 *   limit: 50 
 * });
 * ```
 */
export async function getUsers(params: GetUsersParams = {}): Promise<UsersResponse> {
  try {
    const response = await api.get('/users', {
      params: {
        page: params.page || 1,
        limit: params.limit || 10,
        ...(params.search && { search: params.search }),
        ...(params.role && { role: params.role }),
        ...(params.isActive !== undefined && { isActive: params.isActive }),
      },
    });

    // api.get() returns JSON directly, not wrapped in axios response
    // Response structure: { success: true, data: { users: [], pagination: {} } }
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch users');
    }

    // Return the data: { users: [], pagination: {} }
    return response.data;
  } catch (error) {
    console.error('Get users error:', error);
    throw error;
  }
}

/**
 * Get single user by ID
 * 
 * @param userId - User ID to fetch
 * @returns Promise with user data
 * 
 * @example
 * ```tsx
 * const user = await usersApi.getUser('user-id-123');
 * ```
 */
export async function getUser(userId: string): Promise<User> {
  try {
    const response = await api.get(`/users/${userId}`);

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch user');
    }

    return response.data;
  } catch (error) {
    console.error('Get user error:', error);
    throw error;
  }
}

/**
 * Create new user
 * 
 * @param userData - User data to create
 * @returns Promise with created user data
 * 
 * @example
 * ```tsx
 * const newUser = await usersApi.createUser({
 *   name: 'Dr. John Doe',
 *   email: 'john@moh.gov.sa',
 *   role: 'DOCTOR',
 *   specialty: 'CARDIOLOGY'
 * });
 * ```
 */
export async function createUser(userData: CreateUserData): Promise<User> {
  try {
    const response = await api.post('/users', userData);

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to create user');
    }

    return response.data;
  } catch (error) {
    console.error('Create user error:', error);
    throw error;
  }
}

/**
 * Update user information
 * 
 * @param userId - User ID to update
 * @param userData - Updated user data
 * @returns Promise with updated user data
 * 
 * @example
 * ```tsx
 * const updatedUser = await usersApi.updateUser('user-id-123', {
 *   name: 'Updated Name',
 *   specialty: 'NEUROLOGY'
 * });
 * ```
 */
export async function updateUser(userId: string, userData: UpdateUserData): Promise<User> {
  try {
    const response = await api.put(`/users/${userId}`, userData);

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to update user');
    }

    return response.data;
  } catch (error) {
    console.error('Update user error:', error);
    throw error;
  }
}

/**
 * Deactivate user account
 * 
 * @param userId - User ID to deactivate
 * @returns Promise with deactivation result
 * 
 * @example
 * ```tsx
 * await usersApi.deactivateUser('user-id-123');
 * ```
 */
export async function deactivateUser(userId: string): Promise<void> {
  try {
    const response = await api.delete(`/users/${userId}`);

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to deactivate user');
    }
  } catch (error) {
    console.error('Deactivate user error:', error);
    throw error;
  }
}

/**
 * Activate user account
 * 
 * @param userId - User ID to activate
 * @returns Promise with activation result
 * 
 * @example
 * ```tsx
 * await usersApi.activateUser('user-id-123');
 * ```
 */
export async function activateUser(userId: string): Promise<void> {
  try {
    const response = await api.post(`/users/${userId}/activate`, {});

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to activate user');
    }
  } catch (error) {
    console.error('Activate user error:', error);
    throw error;
  }
}

/**
 * Change user role
 * 
 * @param userId - User ID to change role for
 * @param role - New role to assign
 * @returns Promise with role change result
 * 
 * @example
 * ```tsx
 * await usersApi.changeUserRole('user-id-123', 'COORDINATOR');
 * ```
 */
export async function changeUserRole(userId: string, role: string): Promise<void> {
  try {
    const response = await api.put(`/users/${userId}/role`, { role });

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to change user role');
    }
  } catch (error) {
    console.error('Change user role error:', error);
    throw error;
  }
}

/**
 * Get user sessions
 * 
 * @param userId - User ID to fetch sessions for
 * @returns Promise with user sessions data
 * 
 * @example
 * ```tsx
 * const sessions = await usersApi.getUserSessions('user-id-123');
 * ```
 */
export async function getUserSessions(userId: string): Promise<UserSession[]> {
  try {
    const response = await api.get(`/users/${userId}/sessions`);

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to fetch user sessions');
    }

    return response.data;
  } catch (error) {
    console.error('Get user sessions error:', error);
    throw error;
  }
}

/**
 * Terminate user sessions
 * 
 * @param userId - User ID to terminate sessions for
 * @returns Promise with termination result
 * 
 * @example
 * ```tsx
 * await usersApi.terminateUserSessions('user-id-123');
 * ```
 */
export async function terminateUserSessions(userId: string): Promise<void> {
  try {
    const response = await api.delete(`/users/${userId}/sessions`);

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to terminate user sessions');
    }
  } catch (error) {
    console.error('Terminate user sessions error:', error);
    throw error;
  }
}

// =============================================================================
// API OBJECT
// =============================================================================

/**
 * Users API object with all user management functions
 */
export const usersApi = {
  getUsers,
  getUser,
  createUser,
  updateUser,
  deactivateUser,
  activateUser,
  changeUserRole,
  getUserSessions,
  terminateUserSessions,
};